import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, ShieldAlert, PhoneForwarded } from "lucide-react";
import { PortalPage, Card } from "@/components/PortalPage";
import { Paragraphs, Tabs } from "@/components/ContentParts";
import { api } from "@/lib/api";
import guides from "@/content/guides.json";

const { chapters, dailyHabits, HABIT_BORDER, nonNegotiables, tenets, escalations } = guides.employee;
const TABS = [{ key: "chapters", label: "CHAPTERS" }, { key: "daily", label: "DAILY HABITS" }, { key: "code", label: "THE ALR CODE" }];

const Chapters = () => {
  const [i, setI] = useState(0);
  const c = chapters[i];
  const go = (n) => { setI(n); window.scrollTo(0, 0); };
  return (
    <>
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {chapters.map((ch, n) => (
          <button key={ch.id} onClick={() => go(n)} data-testid={`chapter-pill-${ch.id}`} className={`shrink-0 rounded-lg border px-3 py-2 font-mono text-xs font-bold ${n === i ? "border-[var(--accent)] text-[var(--accent)]" : "border-[#333] text-neutral-500"}`}>{ch.num}</button>
        ))}
      </div>
      <Card className="p-6" data-testid={`chapter-${c.id}`}>
        <p className="text-[10px] font-bold tracking-[0.3em] text-[var(--accent)]">CHAPTER {c.num} · {c.tag}</p>
        <h2 className="mt-2 font-display text-3xl font-extrabold uppercase leading-tight sm:text-4xl">{c.title}</h2>
        <p className="mt-1 text-neutral-400">{c.sub}</p>
        <div className="mt-5 text-[15px] leading-7 text-neutral-200"><Paragraphs text={c.body} /></div>
        {c.callout && <p className="mt-4 rounded-xl border-l-4 border-[var(--accent)] bg-[var(--accent)]/10 p-4 font-semibold">{c.callout}</p>}
        {c.bullets && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {c.bullets.map((b) => <div key={b.heading} className="rounded-xl border border-[#333] p-4"><p className="font-bold text-[var(--accent)]">{b.heading}</p><p className="mt-1 text-sm leading-6 text-neutral-300">{b.text}</p></div>)}
          </div>
        )}
      </Card>
      <div className="flex gap-3">
        <button disabled={i === 0} onClick={() => go(i - 1)} data-testid="chapter-prev" className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-[#333] py-3 text-sm font-bold disabled:opacity-30"><ChevronLeft size={16} /> Previous</button>
        <button disabled={i === chapters.length - 1} onClick={() => go(i + 1)} data-testid="chapter-next" className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-[var(--accent)] py-3 text-sm font-bold text-black disabled:opacity-30">Next <ChevronRight size={16} /></button>
      </div>
    </>
  );
};

const Daily = () => (
  <div className="grid gap-3 sm:grid-cols-2" data-testid="daily-habits">
    {dailyHabits.map((d, i) => (
      <Card key={d.time} className="p-5" style={{ borderColor: HABIT_BORDER[i] === "#58CD36" ? "var(--accent)" : HABIT_BORDER[i] }}>
        <p className="flex items-center gap-2 font-display text-xl font-bold uppercase"><Clock size={18} />{d.time}</p>
        <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-6 text-neutral-300">{d.habits.map((h) => <li key={h}>{h}</li>)}</ul>
      </Card>
    ))}
  </div>
);

const Code = () => (
  <>
    <Card className="p-6" data-testid="alr-code-tenets">
      <p className="mb-3 font-display text-xl font-bold uppercase">The ALR Code</p>
      {tenets.map((t) => <p key={t.num} className="flex gap-4 border-b border-[#2a2a2a] py-3 last:border-0"><span className="font-mono font-bold text-[var(--accent)]">{t.num}</span><span className="text-[15px]">{t.line}</span></p>)}
    </Card>
    <Card className="p-6" data-testid="non-negotiables">
      <p className="mb-3 flex items-center gap-2 font-display text-xl font-bold uppercase"><ShieldAlert size={20} className="text-[#FF3333]" />Non-negotiables</p>
      {nonNegotiables.map(([k, v]) => <div key={k} className="border-b border-[#2a2a2a] py-3 last:border-0"><p className="font-bold">{k}</p><p className="text-sm text-neutral-400">{v}</p></div>)}
    </Card>
    <Card className="p-6" data-testid="escalations">
      <p className="mb-3 flex items-center gap-2 font-display text-xl font-bold uppercase"><PhoneForwarded size={20} className="text-[var(--accent)]" />When to escalate</p>
      {escalations.map(([k, v]) => <div key={k} className="border-b border-[#2a2a2a] py-3 last:border-0"><p className="font-bold">{k}</p><p className="text-sm text-neutral-400">{v}</p></div>)}
    </Card>
  </>
);

export default function EmployeeSuccessGuide() {
  const [tab, setTab] = useState("chapters");
  useEffect(() => { api("/activity/track", { method: "POST", body: { key: "employee_success_guide_viewed" } }).catch(() => {}); }, []);
  return (
    <PortalPage eyebrow="FIELD OPERATOR'S PLAYBOOK" title="Show up like an owner" testId="employee-success-guide-page" width="max-w-4xl">
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === "chapters" && <Chapters />}
      {tab === "daily" && <Daily />}
      {tab === "code" && <Code />}
    </PortalPage>
  );
}
